import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {
  AiRedesignPromptBoxProps,
  EditableComponentsMap,
  RedesignIssue,
  SelectedPromptOptionValues,
  PromptOption,
  PromptAttachment,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {CreditsPillTheme, RedesignIssueType, MoodType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import styles from './ai-redesign-prompt-box.module.scss';
import {AiRedesignPromptBoxIssue, getDefaultPromptOptionValue, getPromptOptionsToUse} from '@Libraries/ai-redesign-prompt-box-library';
import {AiRedesignPromptSuggestions} from '@Components/ai-resdesign-prompt-box/components/ai-redesign-prompt-suggestions';
import {PromptBoxHeader} from '@Components/ai-resdesign-prompt-box/components/prompt-box-header';
import {InputBoxFooter} from '@Components/ai-resdesign-prompt-box/components/input-box-footer';
import {PromptInputField} from '@Components/ai-resdesign-prompt-box/components/prompt-input-field/prompt-input-field';
import {AiRedesignIssue} from '@Components/ai-resdesign-prompt-box/components/ai-redesign-issue';
import {AnimatePresence} from 'framer-motion';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {AiIcon} from '@Components/ai-icon';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {isUserPremium} from '@Libraries/user.library';
import {PromptAttachments} from '@Components/ai-resdesign-prompt-box/components/prompt-attachments';

const AI_REDESIGN_ANIMATION_SHOWN_KEY = 'ai-redesign-prompt-box-animation-shown';

export function AiRedesignPromptBox({
  promptBoxClasses = '',
  containerClasses = '',
  isPromptBoxSelectedOnRender = false,
  unselectOnOutsideClick = false,
  numSuggestionsToShow = 4,
  startsFromIcon = false,
  moodType = MoodType.REDESIGN,
  showRefreshSuggestions = false,
  onStartingIconClicked,
  onContextualMenuOpened,
  onPromptOptionChanged,
  allowPromptAttachments = false,
  onRefreshSuggestions,
  onRecentPromptSelected,
  onPromptSuggestionSelected,
  seasonalPromptPill,
  ...props
}: AiRedesignPromptBoxProps): ReactElement {
  const promptBoxRef = useRef<HTMLDivElement>(null);
  const [editableComponentsMap, setEditableComponentsMap] = useState(props.editableComponentsMap);
  const [isPromptBoxSelected, setIsPromptBoxSelected] = useState(isPromptBoxSelectedOnRender);
  const [prompt, setPrompt] = useState(props.defaultPrompt ?? '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [redesignIssue, setRedesignIssue] = useState(props.redesignIssueOnRender);
  const [isIconExpanded, setIsIconExpanded] = useState(!startsFromIcon);
  const [shouldShowInitialAnimation, setShouldShowInitialAnimation] = useState(false);
  const [promptAttachments, setPromptAttachments] = useState<PromptAttachment[]>([]);

  const resolvedRecentPrompt = typeof props.recentPrompt === 'function' ? props.recentPrompt() : props.recentPrompt;
  const promptOptionsToUse = getPromptOptionsToUse(editableComponentsMap, props.promptOptionsForEditableComponents);

  const getSelectedPromptOptionValues = (): SelectedPromptOptionValues => {
    const selectedPromptOptionValues: SelectedPromptOptionValues = {} as SelectedPromptOptionValues;
    promptOptionsToUse.forEach((option) => {
      selectedPromptOptionValues[option] = props.defaultSelectedPromptOptionValues?.[option] ?? getDefaultPromptOptionValue(option, moodType);
    });

    return selectedPromptOptionValues;
  };

  const [selectedPromptOptionValues, setSelectedPromptOptionValues] = useState<SelectedPromptOptionValues>(getSelectedPromptOptionValues());

  useEffect(() => {
    const newSelectedPromptOptionValues: SelectedPromptOptionValues = {} as SelectedPromptOptionValues;
    promptOptionsToUse.forEach((option) => {
      newSelectedPromptOptionValues[option] = selectedPromptOptionValues[option] ?? getDefaultPromptOptionValue(option, moodType);
    });
    setSelectedPromptOptionValues(newSelectedPromptOptionValues);
  }, [editableComponentsMap, moodType]);

  // selfCodeReview: replace the useState and useEffect for redesignIssue with a use ref
  useEffect(() => {
    setRedesignIssue(props.redesignIssueOnRender);
  }, [props.redesignIssueOnRender]);

  useEffect(() => {
    if (isLoading && props.isEditableDuringGeneration) {
      setIsLoading(false);
    }
  }, [prompt]);

  useEffect(() => {
    if (!props.showInitialAnimation) {
      setShouldShowInitialAnimation(false);
      return;
    }

    const hasAnimationBeenShown = window.sessionStorage.getItem(AI_REDESIGN_ANIMATION_SHOWN_KEY) === 'true';

    if (!hasAnimationBeenShown) {
      setShouldShowInitialAnimation(true);
      window.sessionStorage.setItem(AI_REDESIGN_ANIMATION_SHOWN_KEY, 'true');
    } else {
      setShouldShowInitialAnimation(false);
    }
  }, [props.showInitialAnimation]);

  const handlePromptChange = (prompt: string): void => {
    setPrompt(prompt);
    props.onPromptChange?.(prompt);
  };

  const addPromptAttachment = (attachment: PromptAttachment): void => {
    setPromptAttachments((currentAttachments) => [...currentAttachments, attachment]);
  };

  const removePromptAttachment = (index: number): void => {
    setPromptAttachments((currentAttachments) => currentAttachments.filter((_, i) => i !== index));
  };

  const clearPromptAttachments = (): void => {
    setPromptAttachments([]);
  };

  const updateSelectedPromptOptionValues = (updatedSelectedPromptOptionValues: SelectedPromptOptionValues): void => {
    if (onPromptOptionChanged) {
      (Object.keys(updatedSelectedPromptOptionValues) as PromptOption[]).forEach((promptOption) => {
        const newValue = updatedSelectedPromptOptionValues[promptOption];
        const oldValue = selectedPromptOptionValues[promptOption];

        if (newValue !== oldValue && newValue !== undefined) {
          onPromptOptionChanged(promptOption, newValue);
        }
      });
    }

    setSelectedPromptOptionValues(updatedSelectedPromptOptionValues);
    props.onSelectedPromptOptionValuesChange?.(updatedSelectedPromptOptionValues);
  };

  const updateEditableComponentsMap = (map: EditableComponentsMap): void => {
    setEditableComponentsMap(map);
    props.onEditableComponentsMapChange?.(map);
  };

  const handleError = (error: RedesignIssue | undefined): void => {
    setRedesignIssue(error);
    props.onRedesignIssue?.(error);
  };

  const isSubmissionValid = (): boolean => {
    return !!prompt && !!prompt.replace(/ /g, '').length;
  };

  const handleSubmission = async (): Promise<void> => {
    if (!isSubmissionValid()) {
      return;
    }
    handleError(undefined);
    setIsLoading(true);

    try {
      await props.regenerateContent(prompt, selectedPromptOptionValues, editableComponentsMap, promptAttachments);
      if (!props.isEditableDuringGeneration) {
        handlePromptChange('');
        clearPromptAttachments();
      }
    } catch (e) {
      if (e instanceof AiRedesignPromptBoxIssue) {
        handleError(e as RedesignIssue);
      } else {
        handleError({
          type: RedesignIssueType.ERROR,
          icon: 'icon-exclamation-triangle',
          message: window.i18next.t('pmwjs_unexpected_error_occurred'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmissionWithUpsellCheck = async (): Promise<void> => {
    if (!isSubmissionValid()) {
      return;
    }

    if (props.authenticateModalContent?.showModal) {
      openAuthenticateModal(props.authenticateModalContent.modalProps);
      return;
    }

    const showUpsellDialog = props.upsellDialog ? props.upsellDialog.showDialog : false;
    if (!isUserPremium() && showUpsellDialog) {
      void window.PMW.showPremiumOnlyFeatureDialog(props.upsellDialog?.dialogName ?? '');
      return;
    }

    await handleSubmission();
  };

  const handleIconClick = (): void => {
    onStartingIconClicked?.();
    setIsIconExpanded(!isIconExpanded);
    if (!isIconExpanded) {
      setIsPromptBoxSelected(true);
    }
  };

  const getIconView = (): ReactElement => {
    const title = props.headerContent?.title ?? window.i18next.t('pmwjs_update_text_with_ai');

    const getPulsatingDot = (): ReactElement | undefined => {
      if (!props.headerContent?.showPulsatingDot) {
        return;
      }

      return <PulsatingDot containerClassname={`_position-relative ${styles.pulsatingDot}`} />;
    };

    return (
      <div className={`${styles.iconView} flex-h-row flex-justify-between flex-align-items-center`}>
        <div className="flexbox gap-1 flex-align-items-center">
          <Text val={title} className="content-ai _cursor-pointer" size={TextSize.XSMALL} bold onClick={handleIconClick} />
          {getHeaderIconElement()}
          {getPulsatingDot()}
        </div>
        <AiIcon isActive={isIconExpanded} icon="icon-ai-text" size={IconSize.SIZE_ICON_16} onClick={handleIconClick} />
      </div>
    );
  };

  const getHeaderIconElement = (): ReactElement | undefined => {
    if (!props.headerContent?.icon) {
      return;
    }

    return <Icon icon={props.headerContent.icon} className="content-ai" size={IconSize.SIZE_ICON_16} />;
  };
  const getHeader = (): ReactElement | undefined => {
    if (!props.headerContent) {
      return;
    }

    return <PromptBoxHeader headerContent={props.headerContent} />;
  };

  const showInitialAnimation = (): ReactElement | undefined => {
    if (!shouldShowInitialAnimation) {
      return undefined;
    }

    return <div className={styles.initialAnimation}></div>;
  };

  const handleInputBoxClick = (): void => {
    setIsPromptBoxSelected(true);
  };

  const showPromptAttachments = (): ReactElement | undefined => {
    if (!allowPromptAttachments || promptAttachments.length === 0) {
      return undefined;
    }

    return <PromptAttachments attachments={promptAttachments} removePromptAttachment={removePromptAttachment} />;
  };

  const getPromptInputField = (): ReactElement => {
    return (
      <PromptInputField
        prompt={prompt}
        onPromptChange={handlePromptChange}
        isPromptBoxSelected={isPromptBoxSelected}
        placeholder={props.placeholder}
        handleSubmission={handleSubmissionWithUpsellCheck}
      />
    );
  };

  const getFooter = (): ReactElement => {
    const shouldShowControls = (!isLoading || !!props.isEditableDuringGeneration) && (isPromptBoxSelected || !!prompt);
    return (
      <InputBoxFooter
        showControls={shouldShowControls}
        isLoading={isLoading}
        isSubmitButtonActive={isSubmissionValid()}
        onSubmission={handleSubmission}
        creditsPillTheme={redesignIssue && redesignIssue.type === RedesignIssueType.WARNING ? CreditsPillTheme.WARNING : CreditsPillTheme.SECONDARY}
        promptOptionsToUse={promptOptionsToUse}
        selectedPromptOptionValues={selectedPromptOptionValues}
        onSelectedPromptOptionValuesChange={updateSelectedPromptOptionValues}
        editableComponentsMap={editableComponentsMap}
        onEditableComponentsMapChange={updateEditableComponentsMap}
        upsellDialog={props.upsellDialog}
        creditsPillContent={props.creditsPillContent}
        authenticateModalContent={props.authenticateModalContent}
        isEditableDuringGeneration={props.isEditableDuringGeneration}
        unselectableEditableComponents={props.unselectableEditableComponents}
        moodType={moodType}
        onContextualMenuOpened={onContextualMenuOpened}
        allowPromptAttachments={allowPromptAttachments}
        addPromptAttachment={addPromptAttachment}
      />
    );
  };

  const getInputBox = (): ReactElement => {
    return (
      <div
        className={`${styles.promptBox} radius-12 ${isPromptBoxSelected ? styles.active : ''} ${isLoading ? styles.promptBoxLoading : ''} ${redesignIssue && redesignIssue.type === RedesignIssueType.WARNING ? styles.redesignIssueWarning : ''}`}
        onClick={handleInputBoxClick}
      >
        {showInitialAnimation()}
        {showPromptAttachments()}
        {getPromptInputField()}
        {getFooter()}
      </div>
    );
  };

  const getRedesignIssue = (): ReactElement | undefined => {
    if (!redesignIssue) {
      return;
    }

    return (
      <AiRedesignIssue
        redesignIssue={redesignIssue}
        retry={() => {
          void handleSubmissionWithUpsellCheck();
        }}
      />
    );
  };

  const getPromptSuggestions = (): ReactElement | undefined => {
    if (prompt) {
      return undefined;
    }

    const hasSuggestions = !!props.promptSuggestions || !!resolvedRecentPrompt;
    const shouldShow = hasSuggestions && !redesignIssue && isPromptBoxSelected;

    if (!shouldShow) {
      return undefined;
    }

    return (
      <AiRedesignPromptSuggestions
        promptSuggestions={props.promptSuggestions}
        recentPrompt={resolvedRecentPrompt}
        handlePromptChange={handlePromptChange}
        numSuggestionsToShow={numSuggestionsToShow}
        showRefreshSuggestions={showRefreshSuggestions}
        onRefreshSuggestions={onRefreshSuggestions}
        onRecentPromptSelected={onRecentPromptSelected}
        onPromptSuggestionSelected={onPromptSuggestionSelected}
        seasonalPromptPill={seasonalPromptPill}
      />
    );
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (!unselectOnOutsideClick) {
      return;
    }

    const nextFocused = event.relatedTarget as HTMLElement | null;
    if (!promptBoxRef.current?.contains(nextFocused)) {
      setIsPromptBoxSelected(false);
    }
  };

  const getPromptBox = (): ReactElement => {
    return (
      <div className={containerClasses}>
        {getHeader()}
        <div
          ref={promptBoxRef}
          tabIndex={-1}
          onBlur={handleBlur}
          className={`${promptBoxClasses} ${!props.isEditableDuringGeneration && isLoading ? styles.disabledInteraction : ''} ${styles.promptBoxContainer}`}
        >
          {getInputBox()}
          {getRedesignIssue()}
          <AnimatePresence initial={false}>{getPromptSuggestions()}</AnimatePresence>
        </div>
      </div>
    );
  };

  const getExpandablePromptBoxContent = (): ReactElement | null => {
    if (!isIconExpanded) {
      return null;
    }

    return (
      <div ref={promptBoxRef} tabIndex={-1} onBlur={handleBlur} className={`${promptBoxClasses} ${isLoading ? styles.disabledInteraction : ''} ${styles.promptBoxContainer}`}>
        {getInputBox()}
        {getRedesignIssue()}
        <AnimatePresence initial={false}>{getPromptSuggestions()}</AnimatePresence>
      </div>
    );
  };

  if (startsFromIcon) {
    return (
      <div>
        {getIconView()}
        {getExpandablePromptBoxContent()}
      </div>
    );
  }

  return getPromptBox();
}
